import { useNavigate } from 'react-router-dom';
import { useRoles } from '../lib/configuracion';
import { Escudo, Flecha, Personas } from '../componentes/iconos';

/**
 * Roles y permisos — el listado.
 *
 * ── Por qué tarjetas con ruta propia, y no el panel de dos columnas de antes ──
 * Estaba como maestro-detalle: la lista de roles a la izquierda, los 20 permisos
 * abiertos a la derecha. Tres problemas, y ninguno es de gusto:
 *
 * 1. **No se puede enlazar a un rol.** Los cuatro cuelgan de la misma dirección.
 * 2. **En un teléfono no hay dos columnas**, así que la lista se apilaba encima
 *    del editor y había que desplazarse a ciegas para volver a cambiar de rol.
 * 3. **El editor no tenía sitio.** Con 13 grupos de permisos abiertos, el rol
 *    empujaba la lista fuera de la pantalla.
 *
 * Con la ficha en su propia ruta, el rol tiene dirección, título y espacio para
 * lo único que se hace acá: mirar qué alcance tiene y ajustarlo. Es el criterio
 * de Polaris, que a su vez lo tomó de Medilife.
 *
 * ⚠️ **Acá no se crean roles, y en Polaris sí.** El **ámbito** de un rol
 * —`copathy`, `empresa`, `trabajador`— es lo que usa el motor para sostener la
 * frontera entre empresas. Un rol nuevo obliga a elegirle un ámbito, y elegirlo
 * mal le abre a alguien todas las empresas clientes **sin ningún síntoma**. Los
 * cuatro llegan por migración; lo editable es su alcance.
 */

export const NOMBRE_AMBITO: Record<string, string> = {
  copathy: 'COPATHY',
  empresa: 'Cliente',
  trabajador: 'Trabajador',
};

const AYUDA_AMBITO: Record<string, string> = {
  copathy: 'Ve y atraviesa todos los clientes. Cada cruce queda auditado.',
  empresa: 'Confinado a su propio cliente. Nunca alcanza respuestas individuales.',
  trabajador: 'Solo lo suyo: sus actividades y su propio historial.',
};

export function PanelRoles() {
  const roles = useRoles();
  const ir = useNavigate();

  if (roles.isPending) return <p className="py-16 text-center text-marino-400">Cargando…</p>;
  if (roles.isError) {
    return <p className="py-16 text-center text-red-600">No pudimos cargar los roles.</p>;
  }

  return (
    <>
      <p className="mb-4 rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm text-marino-500">
        Los cuatro roles son fijos. Lo que se ajusta es <strong>qué puede hacer cada uno</strong>:
        crear uno nuevo obligaría a elegirle un ámbito, y elegirlo mal abriría los datos de todos
        los clientes sin que nada falle.
      </p>

      <ul className="space-y-2.5">
        {roles.data?.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => ir(`/configuracion/roles/${r.id}`)}
              className="group flex w-full items-center gap-4 rounded-2xl border border-marino-100
                bg-white p-4 text-left transition hover:border-marino-300 hover:shadow-sm
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
            >
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-marino-50 text-marino-600">
                <Escudo size={22} />
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-marino-800">{r.nombre}</span>
                  <span className="rounded-full bg-marino-50 px-2.5 py-0.5 text-xs font-semibold text-marino-500">
                    {NOMBRE_AMBITO[r.ambito]}
                  </span>
                </span>
                <span className="mt-0.5 block text-sm text-marino-400">
                  {AYUDA_AMBITO[r.ambito]}
                </span>
                <span className="mt-1 flex items-center gap-1.5 text-xs text-marino-400">
                  <Personas size={12} />
                  {r.permisos.length} {r.permisos.length === 1 ? 'permiso' : 'permisos'} ·{' '}
                  {r.usuarios > 0
                    ? `${r.usuarios} ${r.usuarios === 1 ? 'cuenta' : 'cuentas'}`
                    : 'sin cuentas'}
                </span>
              </span>

              <Flecha
                size={16}
                className="shrink-0 -scale-x-100 text-marino-300 transition group-hover:translate-x-0.5 group-hover:text-marino-600"
              />
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}
